import emailjs from '@emailjs/browser';
import { useCallback, useEffect, useState } from 'react';
import { EMAILJS_CONFIG } from '../config/emailjs';
import { useRateLimit } from './useRateLimit';

interface DealerFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
}

interface UseDealerModalReturn {
  isOpen: boolean;
  formData: DealerFormData;
  isSubmitting: boolean;
  isSubmitted: boolean;
  error: string;
  dontShowAgain: boolean;
  isBlocked: boolean;
  remainingTime: number;
  attempts: number;
  maxAttempts: number;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  handleClose: () => void;
  handleDontShowAgainChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  resetForm: () => void;
  clearError: () => void;
}

const DEALER_MODAL_KEY = 'avoprime_dealer_modal_hidden';
const DEALER_TEMPLATE_ID =
  process.env.REACT_APP_EMAILJS_DEALER_TEMPLATE_ID || 'dealer_template_id';

export const useDealerModal = (): UseDealerModalReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState<DealerFormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [emailjsReady, setEmailjsReady] = useState(false);

  // Rate limiting para prevenir spam
  const rateLimit = useRateLimit({
    maxAttempts: 5, // Menos tentativas para o modal
    windowMs: 3600000, // 1 hora
  });

  // Verificar se o modal deve ser exibido ao carregar a página
  useEffect(() => {
    const shouldShowModal = !localStorage.getItem(DEALER_MODAL_KEY);
    if (shouldShowModal) {
      // Aguardar um pouco para não aparecer imediatamente
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Controlar scroll do body quando modal estiver aberto
  useEffect(() => {
    if (isOpen) {
      // Salvar posição atual do scroll
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    } else {
      // Restaurar scroll do body
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // Cleanup
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Inicializar EmailJS
  useEffect(() => {
    try {
      emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
      setEmailjsReady(true);
    } catch (error) {
      setError('Erro na configuração do sistema de e-mail');
      setEmailjsReady(false);
    }
  }, []);

  // Validações de segurança
  const validateFormData = (data: DealerFormData): string | null => {
    // Validação de nome
    if (!data.name.trim() || data.name.length < 2 || data.name.length > 100) {
      return 'Nome deve ter entre 2 e 100 caracteres';
    }

    // Validação de e-mail
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      return 'E-mail inválido';
    }


    // Validação de telefone (opcional)
    if (data.phone && data.phone.length > 20) {
      return 'Telefone muito longo';
    }

    // Validação de empresa (opcional)
    if (data.company && data.company.length > 100) {
      return 'Nome da empresa muito longo';
    }

    return null;
  };

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;

      // Sanitização básica de entrada
      const sanitizedValue = value.replace(
        /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
        ''
      );

      setFormData(prev => ({
        ...prev,
        [name]: sanitizedValue,
      }));
      if (error) setError('');
    },
    [error]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Verificar rate limiting
      if (!rateLimit.checkRateLimit()) {
        const minutes = Math.ceil(rateLimit.remainingTime / 60000);
        setError(`Muitas tentativas. Tente novamente em ${minutes} minutos.`);
        return;
      }

      // Validação dos dados
      const validationError = validateFormData(formData);
      if (validationError) {
        setError(validationError);
        return;
      }

      setIsSubmitting(true);
      setError('');

      try {
        // Verificar se as credenciais do EmailJS estão configuradas
        if (
          !emailjsReady ||
          !EMAILJS_CONFIG.SERVICE_ID ||
          !DEALER_TEMPLATE_ID ||
          !EMAILJS_CONFIG.PUBLIC_KEY ||
          EMAILJS_CONFIG.SERVICE_ID === 'YOUR_SERVICE_ID' ||
          DEALER_TEMPLATE_ID === 'dealer_template_id' ||
          EMAILJS_CONFIG.PUBLIC_KEY === 'YOUR_PUBLIC_KEY'
        ) {
          // Fallback: simular envio para testes
          await new Promise(resolve => setTimeout(resolve, 2000));

        setIsSubmitting(false);
        setIsSubmitted(true);
        rateLimit.recordAttempt();

        // Sempre salvar no localStorage após envio bem-sucedido
        localStorage.setItem(DEALER_MODAL_KEY, 'true');

          return;
        }

        // Envio real via EmailJS
        const templateParams = {
          // Variáveis principais do usuário
          user_name: formData.name.trim(),
          user_email: formData.email.trim().toLowerCase(),
          user_phone: formData.phone.trim() || 'Não informado',
          user_company: formData.company.trim() || 'Não informado',

          // Variáveis adicionais para o template
          date: new Date().toLocaleDateString('pt-BR'),
          time: new Date().toLocaleTimeString('pt-BR'),
          priority: 'Alta',
          source: 'Modal de Revendedor',

          // Variáveis alternativas para compatibilidade
          to_name: 'Avoprime',
          to_email: EMAILJS_CONFIG.TEST_EMAIL,
          from_name: formData.name.trim(),
          from_email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim() || 'Não informado',
          company: formData.company.trim() || 'Não informado',
          subject: 'Nova inscrição de revendedor Avoprime',
        };

        await emailjs.send(
          EMAILJS_CONFIG.SERVICE_ID,
          DEALER_TEMPLATE_ID,
          templateParams,
          EMAILJS_CONFIG.PUBLIC_KEY
        );

        setIsSubmitting(false);
        setIsSubmitted(true);
        rateLimit.recordAttempt();

        // Sempre salvar no localStorage após envio bem-sucedido
        localStorage.setItem(DEALER_MODAL_KEY, 'true');
      } catch (error) {
        setError(
          'Erro ao enviar inscrição. Tente novamente ou entre em contato diretamente.'
        );
        setIsSubmitting(false);
        rateLimit.recordAttempt();
      }
    },
    [formData, rateLimit, emailjsReady, dontShowAgain]
  );

  const handleClose = useCallback(() => {
    // Se marcou para não exibir novamente, salvar no localStorage
    if (dontShowAgain) {
      localStorage.setItem(DEALER_MODAL_KEY, 'true');
    }

    setIsOpen(false);
    // Resetar o formulário após fechar
    setTimeout(() => {
      resetForm();
    }, 300);
  }, [dontShowAgain]);

  const handleDontShowAgainChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDontShowAgain(e.target.checked);
    },
    []
  );

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
    });
    setIsSubmitted(false);
    setError('');
    setDontShowAgain(false);
    rateLimit.resetRateLimit();
  }, [rateLimit]);

  const clearError = useCallback(() => {
    setError('');
  }, []);

  return {
    isOpen,
    formData,
    isSubmitting,
    isSubmitted,
    error,
    dontShowAgain,
    isBlocked: rateLimit.isBlocked,
    remainingTime: rateLimit.remainingTime,
    attempts: rateLimit.attempts,
    maxAttempts: rateLimit.maxAttempts,
    handleInputChange,
    handleSubmit,
    handleClose,
    handleDontShowAgainChange,
    resetForm,
    clearError,
  };
};
